import { ExternalLink, Plus, X } from 'lucide-react';
import { useCanEdit } from '@/auth/account';
import { isHttpUrl, linkCaption } from '@/lib/links';
import { Button } from './ui/Button';
import { TextInput } from './ui/Field';

export interface LinkDraft {
  label: string;
  url: string;
}

interface LinkListProps {
  links: LinkDraft[];
  onChange: (links: LinkDraft[]) => void;
}

/** Opens a link in a new tab without giving the target page access to this app. */
export function OpenLink({ url, label, className }: { url: string; label: string; className?: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} öffnen`}
      title={url}
      className={className ?? 'grid h-8 w-8 shrink-0 place-items-center rounded-md text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--accent)]'}
    >
      <ExternalLink size={15} aria-hidden="true" />
    </a>
  );
}

/** Links of an investment: editable rows for editors, a plain list for viewers. */
export function LinkList({ links, onChange }: LinkListProps) {
  const canEdit = useCanEdit();
  const set = (index: number, patch: Partial<LinkDraft>) =>
    onChange(links.map((link, i) => (i === index ? { ...link, ...patch } : link)));

  if (!canEdit) {
    if (links.length === 0) return <p className="text-xs text-[var(--ink-3)]">Keine Links hinterlegt.</p>;
    return (
      <ul className="flex flex-col gap-1">
        {links.map((link, index) => (
          <li key={index}>
            <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:underline">
              <ExternalLink size={14} aria-hidden="true" />
              {linkCaption(link.label, link.url)}
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {links.map((link, index) => {
        const valid = isHttpUrl(link.url);
        return (
          <div key={index} className="grid items-center gap-2 sm:grid-cols-[12rem_1fr_auto_auto]">
            <TextInput
              aria-label={`Bezeichnung von Link ${index + 1}`}
              placeholder="Bezeichnung (optional)"
              value={link.label}
              onChange={(event) => set(index, { label: event.target.value })}
            />
            <TextInput
              aria-label={`Adresse von Link ${index + 1}`}
              placeholder="https://…"
              inputMode="url"
              value={link.url}
              aria-invalid={link.url.trim() !== '' && !valid}
              onChange={(event) => set(index, { url: event.target.value })}
            />
            {valid ? <OpenLink url={link.url.trim()} label={linkCaption(link.label, link.url)} /> : <span className="hidden w-8 sm:block" />}
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Link ${index + 1} entfernen`}
              onClick={() => onChange(links.filter((_, i) => i !== index))}
              className="h-8 w-8 hover:text-[var(--critical)]"
            >
              <X size={14} />
            </Button>
          </div>
        );
      })}
      <div>
        <Button size="sm" variant="ghost" onClick={() => onChange([...links, { label: '', url: '' }])}>
          <Plus size={14} />
          Link hinzufügen
        </Button>
      </div>
    </div>
  );
}
